<template>
  <div class="document-manager">
    <header class="manager-header">
      <div>
        <div class="eyebrow">Dokumentenablage</div>
        <h2>{{ areaLabel }} verwalten</h2>
        <p>Stelle aktuelle Dokumente bereit. Beim Ersetzen bleibt jede frühere Version nachvollziehbar und abrufbar.</p>
      </div>
      <v-btn color="#58d388" class="black--text create-action" depressed @click="openDialog()">
        <v-icon left>mdi-file-plus-outline</v-icon>Dokument hinzufügen
      </v-btn>
    </header>

    <div v-if="loading" class="document-grid">
      <v-skeleton-loader v-for="index in 4" :key="index" dark type="article, actions" class="document-card" />
    </div>
    <div v-else-if="documents.length" class="document-grid">
      <article v-for="document in documents" :key="document._id" class="document-card" :class="{ inactive: !document.active }">
        <div class="document-heading">
          <span><v-icon>{{ documentIcon(document.currentFile) }}</v-icon></span>
          <div>
            <small>{{ document.category || "Allgemein" }}</small>
            <h3>{{ document.title }}</h3>
          </div>
          <span class="status-badge" :class="{ active: document.active }">{{ document.active ? "Veröffentlicht" : "Entwurf" }}</span>
        </div>
        <p>{{ document.description || "Keine Beschreibung hinterlegt." }}</p>
        <div class="document-meta">
          <span><v-icon>mdi-history</v-icon>Version {{ document.version || 0 }}</span>
          <span><v-icon>mdi-file-multiple-outline</v-icon>{{ (document.versions && document.versions.length) || 0 }} Versionen</span>
        </div>
        <footer>
          <v-btn text color="#58d388" @click="openDialog(document)"><v-icon left>mdi-pencil-outline</v-icon>Bearbeiten</v-btn>
          <v-btn text color="#d4d4d8" :disabled="!document.currentFile" @click="openVersions(document)">
            <v-icon left>mdi-history</v-icon>Versionen
          </v-btn>
          <v-btn icon color="#ff8d98" aria-label="Dokument löschen" @click="deleteDocument(document)"><v-icon>mdi-delete-outline</v-icon></v-btn>
        </footer>
      </article>
    </div>
    <div v-else class="empty-state">
      <v-icon>mdi-folder-plus-outline</v-icon>
      <h3>Noch keine Dokumente</h3>
      <p>Füge das erste Dokument für {{ areaLabel }} hinzu.</p>
    </div>

    <v-dialog v-model="dialog" max-width="44rem" persistent dark scrollable :fullscreen="$vuetify.breakpoint.xsOnly">
      <v-card class="dialog-card">
        <header class="dialog-header">
          <span><v-icon>{{ editingId ? "mdi-file-edit-outline" : "mdi-file-plus-outline" }}</v-icon></span>
          <div>
            <div class="eyebrow">{{ areaLabel }}</div>
            <h2>{{ editingId ? "Dokument bearbeiten" : "Dokument hinzufügen" }}</h2>
            <p>{{ editingId ? "Metadaten ändern oder eine neue Dateiversion hochladen." : "Erfasse das Dokument und lade die erste Version hoch." }}</p>
          </div>
          <v-btn icon aria-label="Dialog schliessen" :disabled="saving" @click="closeDialog"><v-icon>mdi-close</v-icon></v-btn>
        </header>
        <v-card-text class="dialog-body">
          <v-form ref="form" v-model="valid">
            <v-text-field v-model="form.title" label="Titel" outlined :rules="[(value) => !!value || 'Titel erforderlich']" />
            <v-combobox
              ref="categoryInput"
              v-model="form.category"
              :items="categoryOptions"
              :return-object="false"
              :loading="loading"
              label="Thema / Kategorie"
              hint="Vorhandene Kategorie auswählen oder eine neue eingeben."
              persistent-hint
              hide-no-data
              outlined />
            <v-textarea v-model="form.description" label="Kurzbeschreibung (optional)" outlined rows="3" />
            <v-file-input
              v-model="file"
              :label="editingId ? 'Neue Dateiversion (optional)' : 'Datei'"
              :accept="fileAccept"
              :hint="fileFormatsHint"
              persistent-hint
              :prepend-icon="documentIcon(file)"
              show-size
              outlined
              :disabled="saving"
              :rules="[fileRule]" />
            <div class="publish-control" :class="{ active: form.active }">
              <div>
                <strong>Dokument veröffentlichen</strong>
                <span>Nur veröffentlichte Dokumente sind für die Zielgruppe sichtbar.</span>
              </div>
              <v-switch v-model="form.active" color="#58d388" inset hide-details aria-label="Dokument veröffentlichen" />
            </div>
          </v-form>
          <div v-if="saving && uploadProgress > 0" class="upload-progress">
            <div><span>Dokument wird hochgeladen</span><strong>{{ uploadProgress }}%</strong></div>
            <v-progress-linear :value="uploadProgress" color="#009640" background-color="rgba(255,255,255,0.08)" height="8" rounded />
          </div>
          <v-alert v-if="dialogError" type="error" outlined text class="mt-4 mb-0">{{ dialogError }}</v-alert>
        </v-card-text>
        <v-card-actions class="dialog-actions">
          <v-btn text :disabled="saving" @click="closeDialog">Abbrechen</v-btn>
          <v-spacer />
          <v-btn color="#58d388" class="black--text" depressed :disabled="!valid || saving" :loading="saving" @click="saveDocument">
            <v-icon left>mdi-content-save-outline</v-icon>Speichern
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="versionsDialog" max-width="42rem" dark scrollable :fullscreen="$vuetify.breakpoint.xsOnly">
      <v-card v-if="versionDocument" class="dialog-card">
        <header class="dialog-header">
          <span><v-icon>mdi-history</v-icon></span>
          <div>
            <div class="eyebrow">Versionsverlauf</div>
            <h2>{{ versionDocument.title }}</h2>
            <p>Alle bisher hochgeladenen Dateiversionen bleiben abrufbar.</p>
          </div>
          <v-btn icon aria-label="Dialog schliessen" @click="versionsDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </header>
        <v-card-text class="version-list">
          <article v-for="version in versionDocument.versions" :key="version._id" class="version-row">
            <span><v-icon>{{ documentIcon(version.file) }}</v-icon></span>
            <div>
              <strong>Version {{ version.version }}<template v-if="version.version === versionDocument.version"> · Aktuell</template></strong>
              <small>{{ formatDate(version.uploadedAt) }} · {{ version.file && version.file.originalName }}</small>
            </div>
            <v-btn icon aria-label="Version herunterladen" :loading="downloadingId === version._id" @click="downloadVersion(versionDocument, version)">
              <v-icon>mdi-download-outline</v-icon>
            </v-btn>
          </article>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="4000" top right>{{ snackbar.text }}</v-snackbar>
  </div>
</template>

<script>
import api from "@/api"
import { uploadCloudflareMedia } from "@/services/cloudflareMediaUploadService"
import { getAcademyDocumentAccept, getAcademyDocumentFileName, getAcademyDocumentIcon, getAcademyDocumentType } from "@/utils/academyDocumentFiles"

const MAX_FILE_SIZE = 50 * 1024 * 1024

export default {
  name: "DocumentManager",
  props: {
    area: { type: String, default: "general" },
  },
  data() {
    return {
      documents: [],
      loading: false,
      dialog: false,
      versionsDialog: false,
      versionDocument: null,
      editingId: null,
      valid: false,
      form: this.emptyForm(),
      file: null,
      uploadSession: null,
      uploadProgress: 0,
      saving: false,
      downloadingId: "",
      dialogError: "",
      snackbar: { show: false, color: "success", text: "" },
    }
  },
  computed: {
    areaLabel() {
      return this.area === "leadership" ? "Leadership Docs" : "Guidelines"
    },
    categoryOptions() {
      const categories = ["Allgemein", ...this.documents.map((document) => document.category)]
      return [...new Set(categories.map((category) => String(category || "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b, "de-CH"))
    },
    fileAccept() {
      return getAcademyDocumentAccept(this.area)
    },
    fileFormatsHint() {
      return this.area === "leadership" ? "PDF, Word (.doc, .docx) · maximal 50 MB" : "PDF · maximal 50 MB"
    },
    fileRule() {
      return (file) => {
        if (!file) return this.editingId ? true : "Datei erforderlich"
        const type = getAcademyDocumentType(file)
        if (type !== "pdf" && !(this.area === "leadership" && ["doc", "docx"].includes(type))) {
          return this.area === "leadership" ? "Bitte eine PDF- oder Word-Datei (.doc, .docx) wählen" : "Bitte eine PDF-Datei wählen"
        }
        if (!file.size) return "Die Datei ist leer"
        return file.size <= MAX_FILE_SIZE || "Datei maximal 50 MB"
      }
    },
  },
  mounted() {
    this.loadDocuments()
  },
  watch: {
    area() {
      this.loadDocuments()
    },
    file() {
      const session = this.uploadSession
      this.uploadSession = null
      this.uploadProgress = 0
      if (session && this.editingId) api.akademie.cancelAcademyDocumentUpload(this.editingId, session).catch(() => {})
    },
  },
  methods: {
    documentIcon: getAcademyDocumentIcon,
    emptyForm() {
      return { title: "", category: "Allgemein", description: "", active: true }
    },
    notify(color, text) {
      this.snackbar = { show: true, color, text }
    },
    async loadDocuments() {
      this.loading = true
      try {
        this.documents = (await api.akademie.fetchAcademyDocuments(this.area, true)) || []
      } catch (error) {
        this.notify("error", "Die Dokumente konnten nicht geladen werden.")
      } finally {
        this.loading = false
      }
    },
    openDialog(document = null) {
      this.editingId = document && document._id
      this.form = document
        ? { title: document.title || "", category: document.category || "Allgemein", description: document.description || "", active: !!document.active }
        : this.emptyForm()
      this.file = null
      this.uploadSession = null
      this.uploadProgress = 0
      this.dialogError = ""
      this.dialog = true
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.resetValidation()
      })
    },
    closeDialog() {
      if (this.saving) return
      const session = this.uploadSession
      const documentId = this.editingId
      this.uploadSession = null
      this.dialog = false
      if (session && documentId) api.akademie.cancelAcademyDocumentUpload(documentId, session).catch(() => {})
    },
    async saveDocument() {
      if (this.saving) return
      // Freie Eingaben vor dem Speichern in das Combobox-Modell übernehmen.
      if (this.$refs.categoryInput) this.$refs.categoryInput.blur()
      await this.$nextTick()
      if (!this.$refs.form || !this.$refs.form.validate() || this.saving) return
      this.saving = true
      this.dialogError = ""
      let documentId = this.editingId
      try {
        if (!documentId) {
          const created = await api.akademie.createAcademyDocument({ area: this.area, ...this.form, active: false })
          documentId = created._id
          this.editingId = documentId
        }
        const payload = { area: this.area, ...this.form }
        if (this.file) {
          this.uploadSession = await api.akademie.prepareAcademyDocumentUpload(documentId, this.file)
          await uploadCloudflareMedia(this.file, this.uploadSession, {
            onProgress: (loaded, total) => {
              this.uploadProgress = Math.round((loaded * 100) / Math.max(1, total || this.file.size))
            },
          })
          payload.media = this.uploadSession
        }
        await api.akademie.updateAcademyDocument(documentId, payload)
        this.uploadSession = null
        this.dialog = false
        await this.loadDocuments()
        this.notify("success", "Dokument und Versionsverlauf wurden gespeichert.")
      } catch (error) {
        this.dialogError =
          (error.response && error.response.data && (error.response.data.error || error.response.data.message)) ||
          "Das Dokument konnte nicht gespeichert werden."
      } finally {
        this.saving = false
      }
    },
    async deleteDocument(document) {
      if (!confirm(`Dokument «${document.title}» wirklich ausblenden und löschen? Der Dateiverlauf bleibt zur Nachvollziehbarkeit gespeichert.`)) return
      try {
        await api.akademie.deleteAcademyDocument(document._id)
        await this.loadDocuments()
        this.notify("success", "Dokument gelöscht.")
      } catch (error) {
        this.notify("error", "Das Dokument konnte nicht gelöscht werden.")
      }
    },
    openVersions(document) {
      this.versionDocument = document
      this.versionsDialog = true
    },
    async downloadVersion(document, version) {
      this.downloadingId = version._id
      try {
        await api.akademie.downloadAcademyDocumentVersion(
          document._id,
          version._id,
          getAcademyDocumentFileName(version.file, `${document.title}-v${version.version}`),
        )
      } catch (error) {
        this.notify("error", "Die Dokumentversion konnte nicht geöffnet werden.")
      } finally {
        this.downloadingId = ""
      }
    },
    formatDate(value) {
      return value ? new Date(value).toLocaleString("de-CH", { dateStyle: "medium", timeStyle: "short" }) : "-"
    },
  },
}
</script>

<style scoped>
.document-manager {
  color: #f4f4f5;
  font-size: 1rem;
}

.manager-header,
.dialog-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.eyebrow,
.document-heading small {
  color: #7ee5a6;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.manager-header h2,
.dialog-header h2 {
  margin: 0.1875rem 0 0.25rem;
  color: #fff;
  font-size: 1.375rem;
  line-height: 1.3;
}

.manager-header p,
.dialog-header p {
  margin: 0;
  color: #a3a3a8;
  font-size: 0.875rem;
  line-height: 1.5;
}

.create-action,
.dialog-actions .v-btn {
  min-height: 2.75rem;
  border-radius: 0.8125rem !important;
  font-size: 0.875rem;
  font-weight: 750;
  text-transform: none;
}

.document-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
  margin-top: 1rem;
}

.document-card {
  display: flex;
  min-height: 14rem;
  flex-direction: column;
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.025);
}

.document-card.inactive {
  border-style: dashed;
  opacity: 0.75;
}

.document-heading {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.625rem;
}

.document-heading > span:first-child,
.dialog-header > span,
.version-row > span {
  display: grid;
  place-items: center;
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.13);
}

.document-heading > span:first-child {
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 0.8125rem;
}

.document-heading .v-icon,
.dialog-header > span .v-icon,
.version-row > span .v-icon {
  color: currentColor;
}

.document-heading h3 {
  margin: 0.125rem 0 0;
  color: #fff;
  font-size: 1rem;
  line-height: 1.35;
}

.status-badge {
  padding: 0.375rem 0.625rem;
  border-radius: 999px;
  color: #c7c7cb;
  background: rgba(255, 255, 255, 0.06);
  font-size: 0.75rem;
  font-weight: 750;
}

.status-badge.active {
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.13);
}

.document-card > p {
  flex: 1 1 auto;
  margin: 0.875rem 0;
  color: #a3a3a8;
  font-size: 0.875rem;
  line-height: 1.5;
  white-space: break-spaces;
  overflow-wrap: anywhere;
}

.document-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  color: #8f9095;
  font-size: 0.75rem;
}

.document-meta span {
  display: inline-flex;
  align-items: center;
  gap: 0.3125rem;
}

.document-meta .v-icon {
  color: #8f9095;
  font-size: 1rem;
}

.document-card footer {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-top: 0.75rem;
  padding-top: 0.625rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.document-card footer .v-btn {
  font-size: 0.875rem;
  text-transform: none;
}

.document-card footer .v-btn:last-child {
  margin-left: auto;
}

.empty-state {
  display: grid;
  min-height: 16rem;
  place-items: center;
  align-content: center;
  margin-top: 1rem;
  text-align: center;
}

.empty-state .v-icon {
  color: #7ee5a6;
  font-size: 2.75rem;
}

.empty-state h3 {
  margin: 0.75rem 0 0.25rem;
  color: #fff;
  font-size: 1.125rem;
}

.empty-state p {
  margin: 0;
  color: #a3a3a8;
  font-size: 0.875rem;
}

.dialog-card {
  color: #f4f4f5;
  background: #191919 !important;
  font-size: 1rem;
}

.dialog-header {
  padding: 1.25rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.dialog-header > span {
  width: 2.75rem;
  height: 2.75rem;
  flex: 0 0 2.75rem;
  border-radius: 0.8125rem;
}

.dialog-header > div {
  min-width: 0;
  flex: 1 1 auto;
}

.dialog-body {
  padding: 1.25rem !important;
}

.publish-control {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.875rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.875rem;
  background: rgba(0, 0, 0, 0.14);
}

.publish-control.active {
  border-color: rgba(0, 150, 64, 0.34);
  background: rgba(0, 150, 64, 0.07);
}

.publish-control > div {
  display: grid;
  gap: 0.125rem;
}

.publish-control strong {
  color: #fff;
  font-size: 0.875rem;
}

.publish-control span {
  color: #a3a3a8;
  font-size: 0.75rem;
}

.upload-progress {
  margin-top: 1rem;
}

.upload-progress > div {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  color: #b8b8bc;
  font-size: 0.875rem;
}

.dialog-actions {
  padding: 1rem 1.25rem !important;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.version-list {
  display: grid;
  gap: 0.5rem;
  padding: 1rem 1.25rem !important;
}

.version-row {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.8125rem;
}

.version-row > span {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.75rem;
}

.version-row > div {
  display: grid;
  min-width: 0;
  gap: 0.125rem;
}

.version-row strong {
  color: #fff;
  font-size: 0.875rem;
}

.version-row small {
  overflow: hidden;
  color: #8f9095;
  font-size: 0.75rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 72rem) {
  .document-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 43rem) {
  .manager-header {
    align-items: stretch;
    flex-direction: column;
  }

  .document-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .document-heading {
    grid-template-columns: auto minmax(0, 1fr);
  }

  .status-badge {
    grid-column: 2;
    justify-self: start;
  }
}
</style>
